import React, { useEffect, useState } from "react";
import { createUserPortfolio, getUserPortfolio, UpdateUserPortfolio, useAppDispatch } from "../../../../redux";
import { useSelector } from "react-redux";
import PortfolioModal from "../../modals/PortfolioModal";
import { Edit, Globe, Briefcase } from "lucide-react";
import {  GlobalService } from "../../../shared";
import Button from "../../../../components/ui/Button";
import Card from "../../../../components/ui/card";
import {Badge} from "../../../../components/ui/badge";
import { useTranslation } from "react-i18next";

export default function Portfolio({ userId, showEditIcon = true }: { userId?: string; showEditIcon?: boolean }) {
    const { t } = useTranslation();
    const [showPortfolioModal, setShowPortfolioModal] = useState(false);
    const [editPortfolioData, setEditPortfolioData] = useState(null);
    const [portfolioData, setPortfolioData] = useState([]);
    const [originalPortfolioData, setOriginalPortfolioData] = useState([]);

    const dispatch = useAppDispatch();

    const { portfolio } = useSelector((state: any) => state.professional);
    const { user } = useSelector((state: any) => state.auth);
    const targetUserId = userId || user?.id; // Use passed userId or fallback to current user
    useEffect(() => {
        if (portfolio) {
            setPortfolioData(portfolio);
            setOriginalPortfolioData(portfolio); // <--- capture original state
        }
    }, [portfolio]);
    useEffect(() => {
        getAllPortfolios();
    }, [targetUserId]);

    const getAllPortfolios = async () => {
        await dispatch(getUserPortfolio({ id: targetUserId }));
    };

    const handlePortfolioSave = async (data: any[]) => {
        const originalIds = originalPortfolioData.map(p => p.id);
        const newItems = data.filter(item => !item.id);
        const existingItems = data.filter(item => item.id && originalIds.includes(item.id));
        let updatedCount = 0;

        for (const item of existingItems) {
            const original = originalPortfolioData.find(p => p.id === item.id);

            const isChanged =
                original?.title !== item.title ||
                original?.description !== item.description ||
                original?.link !== item.link ||
                JSON.stringify(original?.technology || []) !== JSON.stringify(item.technology || []);

            if (isChanged) {
                const updatePayload = {
                    userId: { id: targetUserId },
                    title: item.title,
                    description: item.description,
                    link: item.link,
                    technology: item.technology || [],
                };

                await dispatch(UpdateUserPortfolio({ id: item.id, data: updatePayload }));
                updatedCount++;
            }
        }

        // Create new items
        let createdItems = [];
        if (newItems.length > 0) {
            const createPayload = newItems.map(item => ({
                userId: { id: targetUserId },
                title: item.title,
                description: item.description,
                link: item.link,
                technology: item.technology || [],
            }));

            const result = await dispatch(createUserPortfolio({ data: createPayload }));

            if (createUserPortfolio.fulfilled.match(result)) {
                createdItems = result.payload;
            }
        }

        // Update local UI state
        const updatedData = [...data.filter(item => item.id && originalIds.includes(item.id)), ...createdItems];
        setPortfolioData(updatedData);
        setOriginalPortfolioData(updatedData); // <--- sync original to prevent false diffs
        if (updatedCount > 0 && newItems.length === 0) {
            GlobalService.showToast("success", t("toast.titles.success"), t("toast.portfolio.updated"));
        }
    };

    useEffect(() => {
        if (!portfolio) return;

        setPortfolioData(portfolio);
    }, [portfolio]);

    return (
        <>
            <Card color={undefined} className="p-6">
                <div className="flex justify-between items-start mb-4">
                    <h2 className="text-xl font-semibold">{t("portfolio.title")}</h2>
                    {showEditIcon && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="text-blue-600 hover:text-blue-700"
                            onClick={() => {
                                setEditPortfolioData(portfolioData);
                                setShowPortfolioModal(true);
                            }}>
                            <Edit className="w-4 h-4" />
                        </Button>
                    )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {portfolioData?.map((project, index) => (
                        <div key={index} className="border rounded-xl overflow-hidden">
                            <img
                                src={
                                    "https://bloomidea.com/sites/default/files/styles/og_image/public/blog/Tipos%20de%20come%CC%81rcio%20electro%CC%81nico_0.png?itok=jC9MlQZq"
                                }
                                alt={project?.title}
                                className="w-full h-48 object-cover"
                            />
                            <div className="p-4">
                                <h3 className="font-medium text-lg mb-2">{project.title}</h3>
                                <p className="text-gray-600 text-sm mb-4">{project.description}</p>
                                <div className="flex flex-wrap gap-2 mb-4">
                                    {project?.technology?.map((tech, i) => (
                                        <Badge variant={undefined} key={i} className="bg-blue-50 text-blue-700">
                                            {tech}
                                        </Badge>
                                    ))}
                                </div>
                                <Button
                                    variant="outline"
                                    className="w-full flex items-center justify-center gap-2"
                                    onClick={() => window.open(project.link, "_blank")}>
                                    <Globe className="w-4 h-4" />
                                    {t("portfolio.viewProject")}
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            </Card>
            <PortfolioModal
                open={showPortfolioModal}
                onClose={() => setShowPortfolioModal(false)}
                onSave={handlePortfolioSave}
                initialData={editPortfolioData}
            />
        </>
    );
}
